'use client';
import React, { useEffect, useRef } from 'react';
import { prefersReducedMotion } from '@utils/prefersReducedMotion';

interface StepperArrowProps {
  steps: string[];
  active: number;
  onChange: (index: number) => void;
  id?: string;
  className?: string;
  /** Scroll behavior when bringing the active item into view (defaults to 'smooth') */
  scrollBehavior?: 'auto' | 'smooth';
  /** Alignment for scrollIntoView inline/block. Use 'center'|'nearest'|'start'|'end' (defaults to 'center'). */
  scrollAlignment?: 'center' | 'nearest' | 'start' | 'end';
}

const StepperArrow: React.FC<StepperArrowProps> = ({
  steps,
  active,
  onChange,
  id,
  className,
  scrollBehavior = 'smooth',
  scrollAlignment = 'center',
}) => {
  const listRef = useRef<HTMLOListElement | null>(null);
  const activeItemRef = useRef<HTMLLIElement | null>(null);
  // don't auto-scroll on the very first render so pages like /design-system start at the top
  const initialMountRef = useRef(true);

  useEffect(() => {
    const el = activeItemRef.current;
    const list = listRef.current;
    if (!el || !list) return;

    // on initial mount, avoid auto-scrolling when the default active is 0
    // If the initial active index is 0, ensure the list is positioned at the start
    // and skip the scroll-into-view behavior. For non-zero initial active values
    // don't scroll to start first (that causes a visual jump) — allow the
    // subsequent scrollIntoView to bring the active item into view.
    if (initialMountRef.current) {
      initialMountRef.current = false;
      if (active === 0) {
        try {
          list.scrollLeft = 0;
        } catch {}
        return; // nothing more to do on initial mount when already at first item
      }
      // otherwise continue and perform the initial scroll for non-default active
    }

    try {
      el.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : scrollBehavior,
        block: 'nearest',
        inline: scrollAlignment,
      } as ScrollIntoViewOptions);
    } catch (err) {
      // Use the error variable to avoid unused-var warnings and fallback for older browsers
      console.warn(err);
      const rect = el.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      const offset = rect.left - listRect.left - list.clientWidth / 2 + rect.width / 2;
      list.scrollLeft += offset;
    }
  }, [active, scrollBehavior, scrollAlignment]);

  // Reveal list items after layout (CSS hides children until `.measured` to avoid flash).
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    let timeout: number | null = null;

    const markMeasured = () => {
      list.classList.add('measured');
    };

    const scheduleMeasured = () => {
      if (timeout) window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        timeout = null;
        markMeasured();
      }, 80);
    };

    scheduleMeasured();

    const fontsReady = (document as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts?.ready;
    if (fontsReady && typeof (fontsReady as Promise<unknown>).then === 'function') {
      (fontsReady as Promise<unknown>).then(() => scheduleMeasured()).catch(() => {});
    }

    return () => {
      if (timeout) window.clearTimeout(timeout);
    };
  }, [steps]);

  if (!Array.isArray(steps) || steps.length === 0) return null;

  function onKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      onChange(Math.min(active + 1, steps.length - 1));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onChange(Math.max(active - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(steps.length - 1);
    }
  }

  return (
    <div
      className={['stepper stepper-arrow', className].filter(Boolean).join(' ')}
      data-stepper
      id={id}
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-label='Stepper navigation'
    >
      <ol ref={listRef} className='stepper-list' role='list'>
        {steps.map((step, i) => (
          <li
            key={step}
            ref={i === active ? activeItemRef : undefined}
            className={`stepper-step${i === active ? ' is-active' : ''}`}
            data-step={i + 1}
            aria-current={i === active ? 'step' : undefined}
            onClick={(e) => {
              onChange(i);

              // also smooth-scroll the clicked item immediately (respect prefers-reduced-motion)
              try {
                (e.currentTarget as HTMLLIElement).scrollIntoView({
                  behavior: prefersReducedMotion() ? 'auto' : scrollBehavior,
                  block: 'nearest',
                  inline: scrollAlignment,
                } as ScrollIntoViewOptions);
              } catch {
                // fallback for older browsers
                const el = e.currentTarget as HTMLLIElement;
                const list = listRef.current;
                if (list && el) {
                  const rect = el.getBoundingClientRect();
                  const listRect = list.getBoundingClientRect();
                  const offset = rect.left - listRect.left - list.clientWidth / 2 + rect.width / 2;
                  list.scrollLeft += offset;
                }
              }
            }}
          >
            <span className='stepper-step-label'>
              {/* <span className='dot'>{i + 1}</span> */}
              {step}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default StepperArrow;
